import * as Sentry from '@sentry/nextjs';
import getenv from 'getenv';
import ReCAPTCHA from 'react-google-recaptcha';
import type { FormikConfig } from 'formik';
import { Field, Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import Button from '@tmh-website/ui/Button';
import FormSection from '@tmh-website/ui/FormSection';
import Input from '@tmh-website/ui/Input';
import Select from '@tmh-website/ui/Select';
import Textarea from '@tmh-website/ui/Textarea';
import type { SelectProps } from '@tmh-website/ui/Select';
import { Breakpoint } from '@tmh-website/shared/const';
import { getCaptchaHlBasedOnLocale } from '@tmh-website/shared/helpers';
import { StyledFormElements } from '@tmh-website/ui/FormElements';
import * as api from '@tmh-website/services/electricity-tariff/electricity-tariff.client.service';
import { SuccessMessage } from '@tmh-website/ui/FormResultMessages';
import type { PostSubmitContactForm } from '@tmh-website/services/electricity-tariff/electricity-tariff.types';

import * as StyledStaticForms from '../StaticForms.styles';

import { ETContactFormFieldLabels, ETContactFormProps, ETContactFormValues } from './ETContactForm.contracts';
import { INITIAL_VALUES as initialValues, validationSchema } from './ETContactForm.config';
import { mapValuesToPayload } from './ETContactForm.helpers';

const ETContactForm = (props: ETContactFormProps) => {
  const { formElementThemeColor, buttonThemeColor, buttonHoverThemeColor } = props;

  const { i18n, t } = useTranslation();
  const { locale } = useRouter();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const fieldLabels: ETContactFormFieldLabels = useMemo(
    () => ({
      firstName: t('global:label.firstName'),
      lastName: t('global:label.lastName'),
      phone: t('global:label.phone'),
      email: t('global:label.email'),
      issueType: t('blocks:staticForms.issueTypeLabel'),
      message: t('global:label.yourMessage'),
      submit: t('global:send'),
    }),
    []
  );

  /**
   * Resolves the options/values for the `<Select>` component that handles the `issue_type` field.
   */
  const resolveIssueTypeOptions = (): SelectProps['options'] => {
    return t('blocks:staticForms.issueTypeOptionsETContact', { returnObjects: true }) as SelectProps['options'];
  };

  /**
   * Submits the form.
   *
   * ℹ️ Google reCAPTCHA validation is always required, regardless of the environment that the form is being used on.
   */
  const submitForm = async (values: ETContactFormValues): Promise<PostSubmitContactForm['response']['success']> => {
    const payload: PostSubmitContactForm['props'] = mapValuesToPayload(values, props);
    return api.postSubmitContactForm(payload);
  };

  /**
   * Determines whether the passed-in argument is an instance of the `Error` class
   * and has its associated error message.
   *
   * @param error - The subject to verify.
   *
   * @returns - `true` is the passed-in argument represents the Error class AND has non-empty message; `false` otherwise.
   */
  const hasErrorMessage = (error: unknown): error is Error => {
    return error instanceof Error && typeof error.message === 'string' && error.message.length > 0;
  };

  /**
   * Handles the `onSubmit` event fired by `<Formik>`.
   */
  const handleOnSubmit: FormikConfig<ETContactFormValues>['onSubmit'] = async values => {
    setError(null);
    setIsLoading(true);

    try {
      await submitForm(values);
      setIsSent(true);
    } catch (error) {
      Sentry.captureException(error);

      if (hasErrorMessage(error)) {
        setError(error.message);
      } else {
        setError(t('global:errorUnknown'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isCurrentLocaleFr: boolean = i18n.language === 'fr';

  const FieldFirstName = () => (
    <Field
      as={Input}
      themeColor={formElementThemeColor}
      label={fieldLabels.firstName}
      name='firstName'
      autoComplete='given-name'
      maxLength={40}
      required
    />
  );

  const FieldLastName = () => (
    <Field
      as={Input}
      themeColor={formElementThemeColor}
      label={fieldLabels.lastName}
      name='lastName'
      autoComplete='family-name'
      maxLength={40}
      required
    />
  );

  return (
    <Formik<ETContactFormValues> {...{ initialValues, validationSchema }} onSubmit={handleOnSubmit}>
      {({ setFieldValue, touched, errors }) => {
        return (
          <Form>
            <>
              <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
                {isCurrentLocaleFr ? (
                  <>
                    <FieldLastName />
                    <FieldFirstName />
                  </>
                ) : (
                  <>
                    <FieldFirstName />
                    <FieldLastName />
                  </>
                )}
              </FormSection>

              <FormSection numberOfColumns={1}>
                {/* Phone */}
                <Field
                  as={Input}
                  themeColor={formElementThemeColor}
                  label={fieldLabels.phone}
                  name='phone'
                  autoComplete='phone'
                  type='tel'
                  maxLength={40}
                />
                {/* END | Phone */}

                {/* E-mail */}
                <Field
                  as={Input}
                  themeColor={formElementThemeColor}
                  label={fieldLabels.email}
                  name='email'
                  autoComplete='email'
                  type='email'
                  maxLength={80}
                  required
                />
                {/* END | E-mail */}

                {/* Issue type */}
                <Field
                  as={Select}
                  themeColor={formElementThemeColor}
                  label={fieldLabels.issueType}
                  name='issueType'
                  options={resolveIssueTypeOptions()}
                  required
                />
                {/* END | Issue type */}

                {/* Message */}
                <Field
                  as={Textarea}
                  themeColor={formElementThemeColor}
                  label={fieldLabels.message}
                  name='message'
                  rows='4'
                  maxLength={8000}
                  required
                />
                {/* END | Message */}
              </FormSection>

              {/* Google reCAPTCHA */}
              <StyledStaticForms.CaptchaContainer>
                <ReCAPTCHA
                  hl={getCaptchaHlBasedOnLocale(locale)}
                  onChange={token => setFieldValue('gRecaptchaResponse', token)}
                  sitekey={getenv('NEXT_PUBLIC_RECAPTCHA_SITE_KEY', process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY)}
                  size='normal'
                />

                {touched.gRecaptchaResponse && errors.gRecaptchaResponse && (
                  <StyledFormElements.ErrorMessage>{t('global:errorMissingCaptcha')}</StyledFormElements.ErrorMessage>
                )}
              </StyledStaticForms.CaptchaContainer>
              {/* END | Google reCAPTCHA */}

              {/* "Submit" button */}
              <Button
                disabled={isSent || Object.keys(touched).length === 0 || Object.keys(errors).length > 0}
                hoverThemeColor={buttonHoverThemeColor}
                isLoading={isLoading}
                minWidth
                themeColor={buttonThemeColor}
                type='submit'>
                {t('global:send')}
              </Button>
              {/* END | "Submit" button */}

              {/* Success message/alert/banner */}
              {isSent && <SuccessMessage marginTop>{t('global:contact.thanksMessage')}</SuccessMessage>}

              {/* Error message/alert/banner */}
              {error && <StyledFormElements.ErrorMessage>{error}</StyledFormElements.ErrorMessage>}
            </>
          </Form>
        );
      }}
    </Formik>
  );
};
export default ETContactForm;
